import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import {
  Switch,
  Route,
  Redirect,
  useLocation,
} from 'react-router-dom';
import { useIntl } from 'react-intl';

import { useRoutes, useIsMobileLayout } from '../../hooks';
import authSelectors from '../../redux/auth/auth.selectors';

import LayoutWithHeader from '../../containers/Layouts/LayoutWithHeader';

import Box from '../../components/Styled/Box';
import FormCard from '../../components/Cards/FormCard';
import Loading from '../../components/Loading/Loading';
import Typography from '../../components/Typography/Typography';

const ContactUs = React.lazy(() => import('../../containers/Forms/Register/ContactUsForm'));
const LoginForm = React.lazy(() => import('../../containers/Forms/Register/LoginForm'));
const SignupForm = React.lazy(() => import('../../containers/Forms/Register/SignupForm'));
const PasswordRecoveryEnterEmailForm = React.lazy(() => import('../../containers/Forms/Register/PasswordRecovery__EnterEmailForm'));

const Layout = ({
  title, subtitle, cardProps, children,
}) => {
  const isMobile = useIsMobileLayout();

  const mobile = (
    <Box width='100%' mt='88px' px='24px'>
      <Typography
        block
        variant='h4'
        color='coldgrey600'
      >
        {title}
      </Typography>
      <Typography
        block
        mt={3}
        color='coldgrey600'
        variant='body'
      >
        {subtitle}
      </Typography>
      <Box pt='16px'>
        {children}
      </Box>
    </Box>
  );

  const desktop = (
    <FormCard
      title={title}
      subtitle={subtitle}
      cardProps={{ my: '24px', ...cardProps }}
    >
      {children}
    </FormCard>
  );

  return isMobile ? mobile : desktop;
};

const Register = () => {
  const { formatMessage: f } = useIntl();
  const routes = useRoutes();
  const location = useLocation();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);

  if (isAuthenticated) {
    return <Redirect to={`${routes.home}${location.search || ''}`} />;
  }

  return (
    <Suspense fallback={<Loading />}>
      <LayoutWithHeader>
        <Box display='flex' justifyContent='center'>
          <Switch>
            <Route
              path={routes.login}
              render={(routerProps) => (
                <Layout
                  title={f({ id: 'pages.login.title' })}
                  subtitle={f({ id: 'pages.login.subtitle' })}
                >
                  <LoginForm {...routerProps} />
                </Layout>
              )}
            />

            <Route
              exact
              path={routes.signup}
              render={(routerProps) => (
                <Layout
                  title={f({ id: 'pages.signup.title' })}
                  subtitle={f({ id: 'pages.signup.subtitle' })}
                >
                  <SignupForm {...routerProps} />
                </Layout>
              )}
            />

            <Route
              path={routes.unauthenticatedContact}
              render={(routerProps) => (
                <Layout
                  title={f({ id: 'pages.unauthenticated_contact.title' })}
                  subtitle={f({ id: 'pages.unauthenticated_contact.subtitle' })}
                >
                  <ContactUs {...routerProps} />
                </Layout>
              )}
            />

            <Route
              path={routes.recoverPassword}
              render={(routerProps) => (
                <Layout
                  title={f({ id: 'pages.password_recovery_enter_email.title' })}
                  subtitle={f({ id: 'pages.password_recovery_enter_email.subtitle' })}
                >
                  <PasswordRecoveryEnterEmailForm {...routerProps} />
                </Layout>
              )}
            />

            <Redirect
              from={routes.start}
              to={routes.login}
            />
          </Switch>
        </Box>
      </LayoutWithHeader>
    </Suspense>
  );
};

export default Register;
